<template>
  <div>
    <Nav />
    <!-- 主要内容区 -->
    <div class="flex pt-20 max-w-7xl mx-auto">
      <LeftNav activeItem="orderlist" />
      <main class="flex-1 p-6">
        <div class="max-w-5xl">
          <div class="mb-8">
            <h1 class="text-2xl font-bold text-gray-900">
              {{ currentStatusText }}
            </h1>
            <p class="text-gray-600 mt-1">{{ currentStatusDescription }}</p>
          </div>
          <div class="mb-6">
            <div class="flex space-x-1 mb-6">
              <button
                v-for="option in statusOptions"
                :key="option.key"
                :class="{
                  'px-4 py-2 text-sm rounded-full': true,
                  'text-primary bg-blue-50': currentStatus === option.key,
                  'text-gray-600 hover:text-primary hover:bg-gray-50':
                    currentStatus !== option.key,
                }"
                @click="handleStatusChange(option.key)"
              >
                {{ option.label }}
              </button>
            </div>
            <div
              class="bg-white rounded-lg border border-gray-200 mb-6"
              v-for="order in orderList"
              :key="order.id"
            >
              <div class="p-4 border-b border-gray-100 bg-blue-50 rounded-t-lg">
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-4">
                    <span class="text-gray-600"
                      >订单编号：{{ order.order_number }}</span
                    >
                    <span class="text-gray-600"
                      >租赁时间：{{ order.start_date }} 至
                      {{ order.end_date }}</span
                    >
                  </div>
                  <span
                    :class="[
                      'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium',
                      getStatusStyle(order.order_status),
                    ]"
                  >
                    <div
                      :class="[
                        'w-2 h-2 rounded-full mr-1',
                        getStatusDotStyle(order.order_status),
                      ]"
                    ></div>
                    {{ getStatusText(order.order_status) }}
                  </span>
                </div>
              </div>
              <div class="p-4 border-b border-gray-100">
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-4">
                    <span class="text-gray-500"
                      >收货人：{{ order.recipient_name }}</span
                    >
                    <span class="text-gray-500"
                      >联系电话：{{ order.recipient_phone }}</span
                    >
                    <span class="text-gray-500"
                      >收货地址：{{ order.recipient_address }}</span
                    >
                  </div>
                </div>
              </div>
              <!-- 展现形式切换按钮 -->
              <div
                class="p-2 border-b border-gray-100 flex justify-between items-center"
              >
                <div class="text-sm text-gray-500 pl-2">设备列表</div>
                <div class="flex space-x-2">
                  <button
                    @click="toggleDisplayMode(order.id, 'grid')"
                    :class="[
                      'px-2 py-1 rounded text-xs',
                      getOrderDisplayMode(order.id) === 'grid'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600',
                    ]"
                  >
                    卡片视图
                  </button>
                  <button
                    @click="toggleDisplayMode(order.id, 'table')"
                    :class="[
                      'px-2 py-1 rounded text-xs',
                      getOrderDisplayMode(order.id) === 'table'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600',
                    ]"
                  >
                    列表视图
                  </button>
                </div>
              </div>

              <!-- 卡片网格模式展示 -->
              <div v-if="getOrderDisplayMode(order.id) === 'grid'" class="p-4">
                <div class="grid grid-cols-4 gap-4">
                  <div
                    class="flex items-center space-x-4"
                    v-for="item in order.order_items"
                    :key="item.id"
                  >
                    <div class="w-16 h-16 bg-gray-100 rounded overflow-hidden">
                      <img
                        :src="item.thumbnail_url"
                        :alt="item.name"
                        class="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div class="text-gray-900">
                        {{ item.name }}
                        <p class="text-xs text-gray-500">
                          {{ item.spec_text }}
                        </p>
                      </div>
                      <div class="text-gray-500 text-sm">
                        ¥{{ item.price }} × {{ item.quantity }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 表格列表模式展示 -->
              <div
                v-else-if="getOrderDisplayMode(order.id) === 'table'"
                class="p-4"
              >
                <!-- 表头行 -->
                <div class="flex py-2 border-b border-gray-200 mb-2">
                  <div class="w-1/3 font-medium text-gray-600">设备</div>
                  <div class="w-1/6 font-medium text-gray-600 text-center">
                    金额
                  </div>
                  <div class="w-1/6 font-medium text-gray-600 text-center">
                    数量
                  </div>
                  <div class="w-1/6 font-medium text-gray-600 text-center">
                    天数
                  </div>
                  <div class="w-1/6 font-medium text-gray-600 text-center">
                    小计
                  </div>
                </div>

                <!-- 设备行 -->
                <div
                  class="flex items-center py-3 border-b border-gray-100"
                  v-for="item in order.order_items"
                  :key="item.id"
                >
                  <div class="w-1/3 flex items-center space-x-4">
                    <div class="w-16 h-16 bg-gray-100 rounded overflow-hidden">
                      <img
                        :src="item.thumbnail_url"
                        :alt="item.name"
                        class="w-full h-full object-cover"
                      />
                    </div>
                    <div class="text-gray-900">
                      {{ item.name }}
                      <p class="text-sm text-gray-500">{{ item.spec_text }}</p>
                    </div>
                  </div>
                  <div class="w-1/6 text-center text-gray-600">
                    ¥{{ item.price }}
                  </div>
                  <div class="w-1/6 text-center text-gray-600">
                    {{ item.quantity }}
                  </div>
                  <div class="w-1/6 text-center text-gray-600">
                    {{ item.rental_days }}
                  </div>
                  <div class="w-1/6 text-center text-gray-900 font-medium">
                    ¥{{ item.amount }}
                  </div>
                </div>
              </div>
              <div
                class="flex items-center justify-between p-4 border-t border-gray-100"
              >
                <div>
                  <span class="text-gray-500 mr-4"
                    >共 {{ order.order_items.length }} 种设备</span
                  >
                  <span class="text-gray-500"
                    >租赁：{{ order.rental_days }} 天</span
                  >
                </div>
                <div class="flex items-center space-x-8">
                  <div class="text-right">
                    <div class="text-gray-500">
                      订单金额：<span class="text-xl text-gray-500 mr-4"
                        >¥{{ order.total_amount }}</span
                      >

                      实付金额：<span
                        class="text-xl font-semibold text-gray-900"
                        >¥{{ order.settlement_amount }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="flex items-center justify-end p-4 border-t border-gray-100"
              >
                <div
                  class="flex items-right space-x-3"
                  v-if="order.order_status === 2"
                >
                  <button
                    class="bg-orange-500 text-white px-4 py-2 !rounded-button hover:bg-orange-600 transition-colors whitespace-nowrap text-sm"
                    @click="handlePayClick(order.order_number)"
                  >
                    立即支付
                  </button>
                </div>
                 <div
                  class="flex items-right space-x-3"
                  v-if="order.order_status === 3 && !order.invoice_id"
                >
                  <button
                    class="px-2 py-1 !rounded-button border border-default hover:bg-gray-100 hover:text-black text-default transition-colors whitespace-nowrap text-sm"
                    @click="handleRefundClick(order.order_number)"
                  >
                    申请退款
                  </button>
                </div>
                <div
                  class="flex items-right space-x-3"
                  v-if="order.order_status === 1 && !order.invoice_id"
                >
                  <button
                    class="px-2 py-1 !rounded-button border border-default hover:bg-gray-100 hover:text-black text-default transition-colors whitespace-nowrap text-sm"
                    @click="handleInvoiceClick(order.order_number)"
                  >
                    申请发票
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页组件 -->
        <Pagination
          :total-count="pagination.totalCount"
          :page_current="pagination.pageCurrent"
          :page_size="pagination.pageSize"
          @page-change="handlePageChange"
        />
      </main>
    </div>
    <!-- 页脚 -->
    <Footer />
  </div>
</template>

<script>
import api from "@/utils/api.js";
import { Message } from "element-ui";
import Nav from "@/components/Nav.vue";
import Footer from "@/components/Footer.vue";
import LeftNav from "@/components/LeftNav.vue";
import Pagination from "@/components/Pagination.vue";

export default {
  name: "OrderList",
  components: {
    Nav,
    Footer,
    LeftNav,
    Pagination,
  },
  data() {
    return {
      userInfo: {},
      userName: "",
      orderList: [],
      userAvatar: "",
      // 订单状态选项
      statusOptions: [
        { key: "all", label: "全部状态" },
        { key: "2", label: "待付款" },
        { key: "3", label: "执行中" },
        { key: "1", label: "已完成" },
        { key: "-1", label: "已取消" },
      ],
      // 订单状态文本映射
      statusTextMap: {
        1: "已完成",
        2: "待付款",
        3: "执行中",
        "-1": "已取消",
      },
      // 订单状态样式映射
      statusStyleMap: {
        1: "bg-blue-100 text-blue-800",
        2: "bg-yellow-100 text-yellow-800",
        3: "bg-green-100 text-green-800",
        "-1": "bg-gray-100 text-gray-800",
      },
      // 订单状态点样式映射
      statusDotMap: {
        1: "bg-blue-600",
        2: "bg-yellow-600",
        3: "bg-green-600",
        "-1": "bg-gray-600",
      },
      // 当前选中的状态
      currentStatus: "all",
      // 订单展示模式 - 使用订单ID作为key，值为'grid'或'table'
      orderDisplayModes: {},
      // 默认显示模式
      defaultDisplayMode: "grid",
      // 分页相关数据
      pagination: {
        totalCount: 0, // 总订单数
        pageCurrent: 1, // 当前页码
        pageSize: 10, // 每页显示数量
      },
    };
  },
  computed: {
    // 当前状态显示文本
    currentStatusText() {
      const statusOption = this.statusOptions.find(
        (option) => option.key === this.currentStatus
      );
      return statusOption ? `租赁${statusOption.label}` : "租赁订单";
    },

    // 当前状态描述文本
    currentStatusDescription() {
      switch (this.currentStatus) {
        case "all":
          return "查看和管理您的所有租赁订单";
        case "2":
          return "查看和管理您需要付款的租赁订单";
        case "3":
          return "查看和管理您当前正在进行中的租赁订单";
        case "1":
          return "查看和管理您已完成的租赁订单";
        case "-1":
          return "查看和管理您已取消的租赁订单";
        default:
          return "查看和管理您的租赁订单";
      }
    },
  },
  mounted() {
    // 加载订单列表数据
    this.loadOrderList();
  },
  methods: {
    // 返回上一页
    goBack() {
      this.$router.back();
    },

    // 加载订单列表
    async loadOrderList() {
      try {
        // 构建请求参数
        const params = {
          page_current: this.pagination.pageCurrent,
          page_size: this.pagination.pageSize,
        };

        // 只有当状态不是"all"时，才添加status参数
        if (this.currentStatus !== "all") {
          params.order_status = this.currentStatus;
        }

        // 调用API获取订单列表
        const response = await api.order.getOrderList(params);

        // 更新订单列表数据
        this.orderList = response.data.resdata || [];

        // 更新总订单数
        this.pagination.totalCount = response.data.restotal || 0;
      } catch (error) {
        console.error("加载订单列表失败:", error);
      }
    },

    // 处理页码变化
    handlePageChange(page) {
      this.pagination.pageCurrent = page;
      this.loadOrderList();
    },

    // 处理支付点击
    handlePayClick(orderNumber) {
      this.$router.push({
        name: "Cashier",
        params: {
          orderNumber: orderNumber,
        },
      });
    },

    // 处理发票点击
    handleInvoiceClick(orderNumber) {
      this.$router.push({
        name: "InvoiceAdd",
        params: {
          orderNumber: orderNumber,
        },
      });
    },

    // 处理退款点击
    handleRefundClick(orderNumber) {
      this.$router.push({
        name: "Refund",
        params: {
          orderNumber: orderNumber,
        },
      });
    },

    // 处理状态切换
    handleStatusChange(status) {
      this.currentStatus = status;
      // 切换状态时重置页码到第一页
      this.pagination.pageCurrent = 1;
      this.loadOrderList();
    },

    // 获取订单状态文本
    getStatusText(status) {
      return this.statusTextMap[status] || "未知状态";
    },

    // 获取订单状态样式
    getStatusStyle(status) {
      return this.statusStyleMap[status] || "bg-gray-100 text-gray-800";
    },

    // 获取订单状态点样式
    getStatusDotStyle(status) {
      return this.statusDotMap[status] || "bg-gray-600";
    },

    // 切换订单设备展示模式
    toggleDisplayMode(orderId, mode) {
      this.$set(this.orderDisplayModes, orderId, mode);
    },

    // 获取订单显示模式，如果未设置则使用默认模式
    getOrderDisplayMode(orderId) {
      return this.orderDisplayModes[orderId] || this.defaultDisplayMode;
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/pages/ucenter/OrderList.vue